import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Database, Menu, X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const PublicNavbar = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/settings/public`);
        setSettings(response.data);
        if (response.data.logo_url) setLogoUrl(response.data.logo_url);
      } catch (error) {
        console.error("Failed to fetch settings");
      }
    };
    fetchSettings();
  }, []);

  const siteName = settings?.site_name || "AIQueryStudio";
  const primaryColor = settings?.primary_color || "#22c55e";

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#09090b]/80 backdrop-blur-xl border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          className="flex items-center justify-between h-16 cursor-pointer"
          onClick={(e) => {
            // Only navigate home if not clicking the mobile menu button
            if (!e.target.closest('button')) {
              navigate('/');
            }
          }}
        >
          {/* Logo */}
          <div className="flex items-center gap-2">
            {logoUrl ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`} alt={siteName} className="w-8 h-8 object-contain" />
            ) : (
              <Database className="w-8 h-8 text-primary" />
            )}
            <span className="text-xl font-bold">{siteName}</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <a href="/#databases" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Databases</a>
            <a href="/#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Features</a>
            <a href="/#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Pricing</a>
            <button onClick={() => navigate('/about')} className="text-sm text-muted-foreground hover:text-foreground transition-colors">About</button>
            <a href="/#support" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Support</a>
            <Button variant="ghost" onClick={() => navigate('/login')}>
              Sign In
            </Button>
            <Button onClick={() => navigate('/register')} style={{ backgroundColor: primaryColor }}>
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 z-10"
            onClick={(e) => {
              e.stopPropagation(); // Prevent navigation to home
              setMobileMenuOpen(!mobileMenuOpen);
            }}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-card">
            <div className="px-4 py-4 space-y-3">
              <a 
                href="/#databases" 
                className="block text-sm text-muted-foreground hover:text-foreground"
                onClick={() => setMobileMenuOpen(false)}
              >
                Databases
              </a>
              <a 
                href="/#features" 
                className="block text-sm text-muted-foreground hover:text-foreground"
                onClick={() => setMobileMenuOpen(false)}
              >
                Features
              </a>
              <a 
                href="/#pricing" 
                className="block text-sm text-muted-foreground hover:text-foreground"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </a>
              <button 
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate('/about');
                }} 
                className="block text-sm text-muted-foreground hover:text-foreground w-full text-left"
              >
                About
              </button>
              <a 
                href="/#support" 
                className="block text-sm text-muted-foreground hover:text-foreground"
                onClick={() => setMobileMenuOpen(false)}
              >
                Support
              </a>
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate('/login');
                }}
              >
                Sign In
              </Button>
              <Button 
                className="w-full" 
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate('/register');
                }} 
                style={{ backgroundColor: primaryColor }}
              >
                Get Started
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default PublicNavbar;
